import React from 'react';
import { Box, Chip, Avatar, Tooltip, useTheme } from '@mui/material';
import {
  EmojiEvents,
  Star,
  Whatshot,
  Diamond,
  Psychology,
  TrendingUp,
  Verified,
  WorkspacePremium,
  Shield,
  Favorite,
  LocalFireDepartment,
  Bolt,
  AutoAwesome,
  Celebration,
  Rocket,
  FlashOn,
  Loyalty,
  Security,
  MilitaryTech,
  CardGiftcard,
  Stars,
  Grade,
  NewReleases,
  Recommend,
  ThumbUp,
  Explore,
  Insights,
  Speed,
  Timeline,
  Palette,
  AdminPanelSettings,
  Work,
  LocalPolice,
  Casino,
  Pets,
  SportsEsports,
  MusicNote,
  Camera,
  Brush,
  Code,
  School,
  Business,
  Public,
  Language,
  Nature,
  FitnessCenter,
  Restaurant,
  Flight,
  DirectionsCar
} from '@mui/icons-material';

const BADGE_TYPES = {
  // User Status Badges
  VERIFIED: { name: 'Verified', icon: Star, color: '#1DA1F2', description: 'Verified user' },
  VIP: { name: 'VIP', icon: Star, color: '#FFD700', description: 'VIP member' },
  PREMIUM: { name: 'Premium', icon: Diamond, color: '#9C27B0', description: 'Premium subscriber' },
  
  // Achievement Badges
  ACHIEVEMENT: { name: 'Achievement', icon: EmojiEvents, color: '#FFD700', description: 'Achievement badge' },
  HYPE_HUNTER: { name: 'Hype Hunter', icon: Whatshot, color: '#FF5722', description: 'Discovers trending content early' },
  TASTE_MAKER: { name: 'Taste Maker', icon: Psychology, color: '#4CAF50', description: 'Excellent content recommendations' },
  INFLUENCER: { name: 'Influencer', icon: TrendingUp, color: '#FF9800', description: '1000+ followers' },
  VETERAN: { name: 'Veteran', icon: EmojiEvents, color: '#795548', description: 'Long-time community member' },
  
  // Special Event Badges
  BETA_TESTER: { name: 'Beta Tester', icon: EmojiEvents, color: '#607D8B', description: 'Helped test new features' },
  EARLY_ADOPTER: { name: 'Early Adopter', icon: Star, color: '#3F51B5', description: 'Joined in the first month' }
};

const ICON_MAP = {
  Star: Star,
  Diamond: Diamond,
  EmojiEvents: EmojiEvents,
  Whatshot: Whatshot,
  Psychology: Psychology,
  TrendingUp: TrendingUp,
  Verified: Verified,
  WorkspacePremium: WorkspacePremium,
  Shield: Shield,
  AdminPanelSettings: AdminPanelSettings,
  Favorite: Favorite,
  LocalFireDepartment: LocalFireDepartment,
  Bolt: Bolt,
  AutoAwesome: AutoAwesome,
  Celebration: Celebration,
  Rocket: Rocket,
  FlashOn: FlashOn,
  Loyalty: Loyalty,
  Security: Security,
  LocalPolice: LocalPolice,
  MilitaryTech: MilitaryTech,
  CardGiftcard: CardGiftcard,
  Stars: Stars,
  Grade: Grade,
  NewReleases: NewReleases,
  Recommend: Recommend,
  ThumbUp: ThumbUp,
  Explore: Explore,
  Insights: Insights,
  Speed: Speed,
  Timeline: Timeline,
  Palette: Palette,
  Work: Work,
  Casino: Casino,
  Pets: Pets,
  SportsEsports: SportsEsports,
  MusicNote: MusicNote,
  Camera: Camera,
  Brush: Brush,
  Code: Code,
  School: School,
  Business: Business,
  Public: Public,
  Language: Language,
  Nature: Nature,
  FitnessCenter: FitnessCenter,
  Restaurant: Restaurant,
  Flight: Flight,
  DirectionsCar: DirectionsCar
};

const BadgeDisplay = ({ badges = [], size = 'small', showTooltip = true, maxDisplay = 3 }) => {
  const theme = useTheme();
  if (!badges || badges.length === 0) return null;

  const displayBadges = badges.slice(0, maxDisplay);
  const remainingCount = badges.length - maxDisplay;

  const renderBadge = (badge, index) => {
    // Use badge data directly if available, otherwise fallback to BADGE_TYPES
    const badgeType = badge.name ? {
      name: badge.name,
      icon: ICON_MAP[badge.icon] || Star,
      color: badge.color || (theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2'),
      description: badge.description || 'User badge'
    } : (BADGE_TYPES[badge.badgeType || badge.type] || {
      name: badge.badgeType || badge.type || 'Badge',
      icon: Star,
      color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2',
      description: 'User badge'
    });
    const IconComponent = badgeType.icon || Star;
    
    const badgeElement = (
      <Chip
        key={badge.id || index}
        size={size}
        avatar={
          <Avatar sx={{ bgcolor: badgeType.color, width: 20, height: 20 }}>
            <IconComponent sx={{ 
              fontSize: 12,
              color: theme.palette.mode === 'dark' ? 'black' : 'white'
            }} />
          </Avatar>
        }
        label={badgeType.name}
        sx={{
          bgcolor: theme.palette.mode === 'dark' 
            ? `${badgeType.color}20`
            : `${badgeType.color}15`,
          color: badgeType.color,
          border: `1px solid ${badgeType.color}${theme.palette.mode === 'dark' ? '40' : '30'}`,
          fontWeight: 600,
          fontSize: size === 'small' ? '0.7rem' : '0.8rem',
          backdropFilter: 'blur(10px)'
        }}
      />
    );

    return showTooltip ? (
      <Tooltip key={badge.id || index} title={badgeType.description} arrow>
        {badgeElement}
      </Tooltip>
    ) : badgeElement;
  };

  return (
    <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
      {displayBadges.map(renderBadge)}
      {remainingCount > 0 && (
        <Chip
          size={size}
          label={`+${remainingCount}`}
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'grey.200',
            color: theme.palette.mode === 'dark' ? 'grey.300' : 'grey.600',
            fontSize: size === 'small' ? '0.7rem' : '0.8rem',
            backdropFilter: 'blur(10px)'
          }}
        />
      )}
    </Box>
  );
};

export default BadgeDisplay;